import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import googleIcon from "../../images/googleIcon.svg";
import axiosInstance from "../../utils/Requests/axios";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import toast from "react-hot-toast";

function SignUp({ formData, setFormData }) {
  const navigate = useNavigate();
  const { t } = useContext(LanguageContext);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validate = () => {
    const newErrors = {};

    // Check for first name
    if (!formData.firstName) {
      newErrors.firstName = "Please enter your first name.";
    }

    // Check for last name
    if (!formData.lastName) {
      newErrors.lastName = "Please enter your last name.";
    }

    // Check for email
    if (!/\S+@\S+\.\S+/.test(formData.username)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.password) {
      newErrors.password = "Password is required.";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long.";
    }

    // Check if confirmPassword is empty
    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required.";
    } else if (confirmPassword !== formData.password) {
      newErrors.confirmPassword = "Passwords do not match.";
    }
    if (!termsAccepted) {
      newErrors.termsAccepted = "You must accept the Terms and Conditions.";
    }
    return newErrors;
  };

  useEffect(() => {
    // Clear errors when the user starts typing
    const validationErrors = validate();
    setErrors(validationErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, confirmPassword, termsAccepted]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitted(true);

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await axiosInstance.post("/auth/register", formData);
      navigate("/verify-email");
    } catch (error) {
      if (error?.status === 409) {
        setErrors({
          email: "An account with this email already exists."
        });
      } else {
        toast.error("Error occured during sign up!");
      }
    }
  };

  return (
    <div
      className="w-100 content-space-t-4 content-space-t-lg-2 content-space-b-1"
      style={{ maxWidth: "25rem" }}
    >
      <form
        className="js-validate needs-validation"
        noValidate=""
        onSubmit={handleSubmit}
      >
        <div className="text-center">
          <div className="mb-5">
            <h1 className="display-5">{t("Create your account")}</h1>
            <p>
              {t("Already have an account?")}{" "}
              {/* eslint-disable-next-line */}
              <a
                className="link"
                onClick={() => navigate("/sign-in")}
                style={{ cursor: "pointer" }}
              >
                {t("Sign in here")}
              </a>
            </p>
          </div>
          <div className="d-grid mb-4">
            <a className="btn btn-white btn-lg" href={`${process.env.REACT_APP_API_BASE_URL}/auth/register/google`}>
              <span className="d-flex justify-content-center align-items-center">
                <img
                  className="avatar avatar-xss me-2"
                  src={googleIcon}
                  alt="Description"
                />
                {t("Sign up with Google")}
              </span>
            </a>
          </div>
          <span className="divider-center text-muted mb-4">{t("OR")}</span>
        </div>
        <label className="form-label" htmlFor="fullNameSrEmail">
          {t("Full name")}
        </label>
        <div className="row">
          <div className="col-sm-6">
            <div className="mb-4">
              <input
                type="text"
                className={`form-control form-control-lg ${
                  submitted && errors.firstName
                    ? "is-invalid"
                    : submitted && formData.firstName
                    ? "is-valid"
                    : ""
                }`}
                name="fullName"
                id="fullNameSrEmail"
                placeholder="Mark"
                aria-label="Mark"
                required=""
                value={formData.firstName}
                onChange={(e) => {
                  const dupObj = { ...formData };
                  dupObj.firstName = e.target.value;
                  setFormData(dupObj);
                }}
              />
              {submitted && errors.firstName && (
                <span className="invalid-feedback">
                  {t(errors.firstName)}
                </span>
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="mb-4">
              <input
                type="text"
                className={`form-control form-control-lg ${
                  submitted && errors.lastName
                    ? "is-invalid"
                    : submitted && formData.lastName
                    ? "is-valid"
                    : ""
                }`}
                placeholder="Williams"
                aria-label="Williams"
                required=""
                value={formData.lastName}
                onChange={(e) => {
                  const dupObj = { ...formData };
                  dupObj.lastName = e.target.value;
                  setFormData(dupObj);
                }}
              />
              {submitted && errors.lastName && (
                <span className="invalid-feedback">
                  {t(errors.lastName)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="mb-4">
          <label className="form-label" htmlFor="signupSrEmail">
            {t("Your email")}
          </label>
          <input
            type="email"
            className={`form-control form-control-lg ${
              submitted && errors.email
                ? "is-invalid"
                : submitted && formData.username
                ? "is-valid"
                : ""
            }`}
            name="email"
            id="signupSrEmail"
            placeholder="Markwilliams@site.com"
            aria-label="Markwilliams@site.com"
            required=""
            value={formData.username}
            onChange={(e) => {
              const dupObj = { ...formData };
              dupObj.username = e.target.value;
              setFormData(dupObj);
            }}
          />
          {submitted && errors.email && (
            <span className="invalid-feedback">
              {t(errors.email)}
            </span>
          )}
        </div>
        <div className="mb-4">
          <label className="form-label" htmlFor="signupSrPassword">
            {t("Password")}
          </label>
          <div
            className={`input-group input-group-merge ${
              submitted && errors.password
                ? "is-invalid"
                : submitted && formData.password
                ? "is-valid "
                : ""
            }`}
            data-hs-validation-validate-class=""
          >
            <input
              type={showPassword ? "text" : "password"}
              className="js-toggle-password form-control form-control-lg"
              name="password"
              placeholder="8+ characters required"
              aria-label="8+ characters required"
              required=""
              data-hs-toggle-password-options='{
              "target": [".js-toggle-password-target-1", ".js-toggle-password-target-2"],
              "defaultClass": "bi-eye-slash",
              "showClass": "bi-eye",
              "classChangeTarget": ".js-toggle-password-show-icon-1"
            }'
              value={formData.password}
              onChange={(e) => {
                const dupObj = { ...formData };
                dupObj.password = e.target.value;
                setFormData(dupObj);
              }}
            />
            <div
              className="js-toggle-password-target-1 input-group-append input-group-text"
              onClick={() => setShowPassword(!showPassword)} // Toggle password visibility
              style={{ cursor: "pointer" }}
            >
              <i
                className={`js-toggle-password-show-icon-1 bi ${
                  showPassword ? "bi-eye" : "bi-eye-slash"
                }`}
              ></i>
            </div>
          </div>

          {submitted && errors.password && (
            <span className="invalid-feedback">
              {t(errors.password)}
            </span>
          )}
        </div>
        <div className="mb-4">
          <label className="form-label" htmlFor="signupSrConfirmPassword">
            {t("Confirm password")}
          </label>
          <div
            className={`input-group input-group-merge ${
              submitted && errors.confirmPassword
                ? "is-invalid"
                : submitted && confirmPassword
                ? "is-valid"
                : ""
            }`}
            data-hs-validation-validate-classname=""
          >
            <input
              type={showConfirmPassword ? "text" : "password"}
              className={`form-control form-control-lg `}
              name="confirmPassword"
              id="signupSrConfirmPassword"
              placeholder="8+ characters required"
              aria-label="8+ characters required"
              required=""
              minLength="8"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <div
              className="js-toggle-password-target-1 input-group-append input-group-text"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)} // Toggle password visibility
              style={{ cursor: "pointer" }}
            >
              <i
                className={`js-toggle-password-show-icon-1 bi ${
                  showConfirmPassword ? "bi-eye" : "bi-eye-slash"
                }`}
              ></i>
            </div>
          </div>

          {submitted && errors.confirmPassword && (
            <span className="invalid-feedback">
              {t(errors.confirmPassword)}
            </span>
          )}
        </div>
        <div className="form-check mb-4">
          <input
            className={`form-check-input ${
              submitted && errors.termsAccepted ? "is-invalid" : ""
            }`}
            type="checkbox"
            value=""
            id="termsCheckbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            required=""
          />
          <label
            className={`form-check-label ${
              submitted && errors.termsAccepted ? "text-danger" : ""
            }`}
            htmlFor="termsCheckbox"
          >
            {t("I accept the")}{" "}
            <a href="https://htmlstream.com/preview/front-dashboard-v2.1.1/authentication-signup-cover.html#">
              {t("Terms and Conditions")}
            </a>
          </label>
          {submitted && errors.termsAccepted && (
            <div className="invalid-check">
              {t("Please accept our Terms and Conditions.")}
            </div>
          )}
        </div>
        <div className="d-grid gap-2">
          <button type="submit" className="btn btn-primary btn-lg">
            {t("Create an account")}
          </button>

          <button type="submit" className="btn btn-link">
            {t("or Start your 30-day trial")}{" "}
            <i className="bu-chevron-right"></i>
          </button>
        </div>
      </form>
    </div>
  );
}

export default SignUp;
